import { Box, InputLabel, TextField, TextFieldProps } from '@mui/material'
import { useApp } from '../../hooks/useApp'
import { FocusEvent, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

export type EnionTextFieldProps = TextFieldProps & {
  oneLine?: boolean
  unit?: string
  min?: number
  max?: number
  integer?: boolean
  positive?: boolean
}

/**
 * Text field with added data-cy.
 */
export const Text = (props: EnionTextFieldProps): JSX.Element => {

  const app = useApp()
  const { t } = useTranslation()
  const textProps = { ...props }
  delete textProps.oneLine
  delete textProps.onBlur
  delete textProps.integer
  delete textProps.positive

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value || '0')
    if (props.max !== undefined && val > props.max) {
      app.addError(t('Value cannot be more than {max}').replace('{max}', `${props.max}`))
      props.onBlur && props.onBlur({
        target: {
          value: `${props.max}`
        }
      } as FocusEvent<HTMLInputElement>)
    } else if (props.min !== undefined && val < props.min) {
      app.addError(t('Value cannot be less than {min}').replace('{min}', `${props.min}`))
      props.onBlur && props.onBlur({
        target: {
          value: `${props.min}`
        }
      } as FocusEvent<HTMLInputElement>)
    } else {
      props.onBlur && props.onBlur(e)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (props.positive && e.code === 'Slash') {
      return e.preventDefault()
    }
    if (props.integer && e.code === 'Period') {
      return e.preventDefault()
    }
    props.onKeyDown && props.onKeyDown(e)
  }

  if (props.oneLine) {
    const inputLabel = <InputLabel>{props.label}</InputLabel>
    delete textProps.label

    const text = <TextField
      autoComplete={props.autoComplete || props.name}
      data-cy={`text-${props.label}`}
      size="small"
      {...textProps}
      onBlur={onBlur}
    />

    return (
      <Box sx={{ ...props.sx, display: 'flex' }}>
        <Box sx={{ pt: 3 }}>{inputLabel}</Box>
        <Box sx={{ pl: 1 }}>{text}</Box>
        <Box sx={{ pt: 2, ml: 1 }}>{props.unit}</Box>
      </Box>
    )
  }

  return <TextField
    autoComplete={props.autoComplete || props.name}
    data-cy={`text-${props.label}`}
    {...textProps}
    onKeyDown={onKeyDown}
    onBlur={onBlur}
  />
}
